import { Link, useParams } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import NewsCard from "@/components/feature/NewsCard";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { newsArticles } from "@/mocks/news";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const article = newsArticles.find((item) => item.id === id);

  useDocumentMeta({
    title: article ? `${article.title} - 出轨的味道资讯专题` : "资讯详情 - 出轨的味道",
    description: article ? article.summary : "出轨的味道资讯专题详情页。",
    keywords: article ? `出轨的味道,${article.category},影视资讯,剧情评论` : "出轨的味道,影视资讯",
  });

  if (!article) {
    return (
      <AppLayout>
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这篇内容</h1>
          <p className="mt-2 text-sm text-foreground-600">文章可能已下线，或者链接有误。</p>
          <Link
            to="/news"
            className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回资讯专题
          </Link>
        </div>
      </AppLayout>
    );
  }

  const others = newsArticles.filter((item) => item.id !== article.id).slice(0, 3);

  return (
    <AppLayout>
      <article className="mx-auto w-full max-w-3xl px-4 py-8 md:px-8 md:py-10">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="transition-colors hover:text-accent-700 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/news" className="transition-colors hover:text-accent-700 cursor-pointer">
            资讯专题
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="line-clamp-1 text-foreground-800">{article.title}</span>
        </nav>

        <header className="mt-5">
          <span className="rounded-full bg-accent-100 px-3 py-1 text-[11px] font-medium text-accent-800">
            {article.category}
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              发布于 {article.publishedAt}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              出轨的味道编辑组
            </span>
          </div>
        </header>

        <div className="mt-6 w-full overflow-hidden rounded-lg" style={{ aspectRatio: "16 / 9" }}>
          <img
            src={article.cover}
            alt={article.title}
            title={article.title}
            className="h-full w-full object-top"
          />
        </div>

        <p className="mt-6 rounded-lg border-l-4 border-accent-500 bg-background-100 px-5 py-4 text-sm leading-7 text-foreground-700">
          {article.summary}
        </p>

        <div className="mt-6 flex flex-col gap-4">
          {article.content.map((paragraph, i) => (
            <p key={i} className="text-sm leading-8 text-foreground-700">
              {paragraph}
            </p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/news"
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-accent-400 hover:text-accent-700 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回列表
          </Link>
          <Link
            to="/category"
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
          >
            <i className="ri-film-line"></i>
            浏览影视分类
          </Link>
        </div>

        <section className="mt-12 border-t border-background-200 pt-8">
          <h2 className="font-heading text-xl font-bold text-foreground-950">相关阅读</h2>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((item) => (
              <NewsCard key={item.id} article={item} />
            ))}
          </div>
        </section>
      </article>
    </AppLayout>
  );
}